
/**
 * 社用車マスタページ
 * 社用車情報の登録・編集・管理
 */

import React from 'react'
import { useCompanyVehicles } from '@/features/masters/hooks/useCompanyVehicles'
import CompanyVehiclesTable from '@/features/masters/components/CompanyVehiclesTable'
import AddCompanyVehicleButton from '@/features/masters/components/AddCompanyVehicleButton'
import { Skeleton } from "@/shared/ui/skeleton"
import { PageLayout } from '@/shared/components'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/shared/ui/breadcrumb'
import { Link } from 'react-router-dom'

/**
 * 社用車マスタページコンポーネント
 */
export default function CompanyVehiclesPage() {
    const { data: vehicles, isLoading, error, createEntity, updateEntity, deleteEntity } = useCompanyVehicles()

    const addVehicle = async (data: import('@/features/masters/types').CompanyVehicleInsert) => {
        const result = await createEntity(data)
        return !!result
    }

    const updateVehicle = async (id: string, data: import('@/features/masters/types').CompanyVehicleUpdate) => {
        const result = await updateEntity(id, data)
        return !!result
    }

    const deleteVehicle = async (id: string) => {
        return await deleteEntity(id)
    }

    if (isLoading) {
        return (
            <PageLayout
                title="社用車マスタ"
                description="社用車情報の登録・編集を行います。"
                actions={<Skeleton className="h-10 w-28" />}
            >
                <Breadcrumb className="mb-6">
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">ホーム</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/masters">マスタ管理</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>社用車マスタ</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
                <Skeleton className="h-[300px] w-full" />
            </PageLayout>
        )
    }

    if (error) {
        return (
            <PageLayout
                title="社用車マスタ"
                description="社用車情報の登録・編集を行います。"
            >
                <Breadcrumb className="mb-6">
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">ホーム</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/masters">マスタ管理</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>社用車マスタ</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
                <div className="text-red-600">
                    <p>データの読み込み中にエラーが発生しました: {typeof error === 'string' ? error : (error as Error)?.message || 'Unknown error'}</p>
                </div>
            </PageLayout>
        )
    }

    return (
        <PageLayout
            title="社用車マスタ"
            description="社用車情報の登録・編集を行います。"
            actions={<AddCompanyVehicleButton onAdd={addVehicle} />}
        >
            <Breadcrumb className="mb-6">
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/">ホーム</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/masters">マスタ管理</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>社用車マスタ</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <CompanyVehiclesTable
                data={vehicles || []}
                isLoading={isLoading}
                onEdit={updateVehicle}
                onDelete={deleteVehicle}
            />
        </PageLayout>
    )
} 